// ============================================
// Mapping: yzx → camera.position.set(y, z, x)
// ============================================

import { GeoAutomationPOI } from '../services/customerApi';
import { ManifestData, localToWGS84 } from './manifestUtils';  // ← ADD localToWGS84 import!
import { wgs84ToLocal } from './poiUtils';

/**
 * Smooth walkthrough to POI using correct camera axis mapping
 * Mapping: yzx → camera.position.set(local.y, local.z, local.x)
 * Camera lands 5m IN FRONT of POI, looking at it
 */
export function walkToPOIWithCamera(
  viewer: any,
  currentPosition: { latitude: number; longitude: number; altitude: number },
  targetPOI: GeoAutomationPOI,
  manifest: ManifestData,
  options: {
    duration?: number;
    offsetDistance?: number;
    onProgress?: (progress: number) => void;
    onComplete?: () => void;
  } = {}
): () => void {
  const {
    duration = 4000, // 4 seconds (slower) - was 2000
    offsetDistance = 5, // 5 meters before POI
    onProgress,
    onComplete
  } = options;

  if (!viewer || !viewer.camera) {
    console.error('[Walkthrough] No camera found on viewer!');
    return () => {};
  }

  const camera = viewer.camera;
  const origin = manifest.observer_coordinates;

  // Convert start position to local coordinates
  const startLocal = wgs84ToLocal(
    currentPosition.latitude,
    currentPosition.longitude,
    currentPosition.altitude,
    origin
  );

  // Convert POI position to local coordinates
  const poiLocal = wgs84ToLocal(
    targetPOI.latitude,
    targetPOI.longitude,
    targetPOI.altitude,
    origin
  );

  // Calculate direction from start to POI
  const dx = poiLocal.x - startLocal.x;
  const dy = poiLocal.y - startLocal.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Normalize direction
  const dirX = dx / distance;
  const dirY = dy / distance;

  // Camera end position: offsetDistance meters BEFORE POI (not past it!)
  // Use MINUS so we stop short of the POI, don't overshoot
  const endLocal = {
    x: poiLocal.x - (dirX * offsetDistance),
    y: poiLocal.y - (dirY * offsetDistance),
    z: poiLocal.z // Same height as POI
  };

  // ========================================
  // ALTITUDE SAFETY: Keep camera at consistent eye level
  // Don't interpolate through ground
  // ========================================
  const eyeLevel = 1.6; // 1.6 meters above ground (human eye height)
  const safeStartZ = Math.max(startLocal.z, eyeLevel);
  const safeEndZ = Math.max(endLocal.z, eyeLevel);

  console.log('[Walkthrough] Starting smooth walk:');
  console.log('  From (local):', startLocal);
  console.log('  POI (local):', poiLocal);
  console.log('  Camera target (local):', endLocal, `(${offsetDistance}m before POI)`);
  console.log('  Safe altitude: start=${safeStartZ.toFixed(2)}m, end=${safeEndZ.toFixed(2)}m');
  console.log('  Duration:', duration + 'ms');

  const startTime = performance.now();
  let animationFrameId: number;
  let cancelled = false;

  // Gentler easing - smooth start and very gentle slow-down at end
  function easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function animate(currentTime: number) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutQuad(progress);

    // Interpolate position in local coordinates
    const x = startLocal.x + (endLocal.x - startLocal.x) * easedProgress;
    const y = startLocal.y + (endLocal.y - startLocal.y) * easedProgress;
    
    // Interpolate altitude with safety margin (prevents ground clipping)
    const z = safeStartZ + (safeEndZ - safeStartZ) * easedProgress;

    // ========================================
    // APPLY CORRECT AXIS MAPPING: yzx
    // camera.position.set(local.y, local.z, local.x)
    // ========================================
    camera.position.set(y, z, x);

    // Make camera look at POI (also using yzx mapping)
    camera.lookAt(poiLocal.y, poiLocal.z, poiLocal.x);

    // Update controls
    if (viewer.controls?.update) {
      viewer.controls.update(0);
    }

    // ========================================
    // CRITICAL FIX: Convert local position to WGS84 for progress callback!
    // ========================================
    if (onProgress) {
      // Convert current camera local position to WGS84
      const currentWGS84 = localToWGS84(x, y, z, origin);
      
      console.log('[Walkthrough] Frame', Math.floor(progress * 100) + '%', 
        'Local:', { x: x.toFixed(2), y: y.toFixed(2), z: z.toFixed(2) },
        'WGS84:', { 
          lat: currentWGS84.latitude.toFixed(7), 
          lon: currentWGS84.longitude.toFixed(7), 
          alt: currentWGS84.altitude.toFixed(2) 
        }
      );
      
      // Pass progress value (0-1) to callback
      // The callback can use this progress however it wants
      onProgress(progress);
    }

    // Continue or complete
    if (progress < 1) {
      animationFrameId = requestAnimationFrame(animate);
    } else {
      console.log('[Walkthrough] Walk complete! You should be 5m before POI, looking at it.');
      if (onComplete) {
        onComplete();
      }
    }
  }

  // Start animation
  animationFrameId = requestAnimationFrame(animate);

  // Return cancel function
  return () => {
    cancelled = true;
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }
    console.log('[Walkthrough] Walk cancelled');
  };
}